import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Separator } from '../../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import {
  Search, Zap, Plus, Printer, FileText, FileSpreadsheet,
  ArrowRightLeft, ArrowUpDown, History, AlertTriangle, ScanLine,
  Archive, Layers
} from 'lucide-react';
import { T } from './constants';

export const LocatorDialog = ({ open, onOpenChange, query, onSearch, results, onHighlight }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="w-5 h-5 text-orange-400" /> Gaseste Articol</DialogTitle></DialogHeader>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input className="w-full h-10 pl-10 pr-4 text-sm rounded-lg bg-zinc-800 border border-zinc-700 text-white outline-none focus:border-blue-500" placeholder="Cauta articol (nume, cod)..." value={query} onChange={e => onSearch(e.target.value)} autoFocus data-testid="locator-search" />
      </div>
      <div className="max-h-64 overflow-y-auto space-y-1.5 mt-2">
        {results.length === 0 && query.length >= 2 && <p className="text-center text-zinc-600 py-4 text-xs">Niciun rezultat</p>}
        {results.map((r, i) => (
          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 cursor-pointer transition-colors" onClick={() => onHighlight(r.bin_id)}>
            <div>
              <p className="text-sm font-medium">{r.article_name || r.article_code}</p>
              <p className="text-[10px] text-zinc-500">{r.bin_code || r.bin_id?.substring(0, 8)}</p>
            </div>
            <div className="text-right">
              <p className="font-mono font-bold text-emerald-400">{r.quantity}</p>
              <p className="text-[10px] text-zinc-500">{r.unit || 'buc'}</p>
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const WizardDialog = ({ open, onOpenChange, step, setStep, data, setData, plants, onComplete }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-blue-400" /> Wizard Creare Dulap - Pas {step}/2</DialogTitle></DialogHeader>
      <div className="flex gap-2 mb-4">{[1,2].map(s => <div key={s} className="flex-1"><div className={`h-1.5 rounded-full ${s <= step ? 'bg-blue-500' : 'bg-zinc-700'}`} /></div>)}</div>
      {step === 1 && (
        <div className="space-y-4">
          <div><Label className="text-zinc-400 text-xs">Fabrica</Label><Select value={data.plant_id} onValueChange={v => setData({...data, plant_id: v})}><SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Selectati" /></SelectTrigger><SelectContent>{plants.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-zinc-400 text-xs">Nume Dulap</Label><Input className="bg-zinc-800 border-zinc-700 text-white" value={data.cabinet_name} onChange={e => setData({...data, cabinet_name: e.target.value.toUpperCase()})} placeholder="DULAP-A" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-zinc-400 text-xs">Rafturi</Label><Input type="number" className="bg-zinc-800 border-zinc-700 text-white" value={data.shelves_count} onChange={e => setData({...data, shelves_count: parseInt(e.target.value)||1})} min="1" /></div>
            <div><Label className="text-zinc-400 text-xs">Pozitii/Raft</Label><Input type="number" className="bg-zinc-800 border-zinc-700 text-white" value={data.positions_per_shelf} onChange={e => setData({...data, positions_per_shelf: parseInt(e.target.value)||1})} min="1" /></div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-4">
          <div className="p-5 rounded-xl text-center" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <p className="text-4xl font-extrabold text-blue-400">{data.shelves_count * data.positions_per_shelf}</p>
            <p className="text-xs text-blue-300 mt-1">pozitii in {data.cabinet_name}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-lg bg-zinc-800"><p className="text-lg font-bold text-indigo-400">1</p><p className="text-[10px] text-zinc-500">Dulap</p></div>
            <div className="p-2 rounded-lg bg-zinc-800"><p className="text-lg font-bold text-green-400">{data.shelves_count}</p><p className="text-[10px] text-zinc-500">Rafturi</p></div>
            <div className="p-2 rounded-lg bg-zinc-800"><p className="text-lg font-bold text-orange-400">{data.positions_per_shelf}</p><p className="text-[10px] text-zinc-500">Poz/Raft</p></div>
          </div>
        </div>
      )}
      <DialogFooter>
        {step > 1 && <Button variant="ghost" className="text-zinc-400" onClick={() => setStep(1)}>Inapoi</Button>}
        <Button variant="ghost" className="text-zinc-400" onClick={() => { onOpenChange(false); setStep(1); }}>Anuleaza</Button>
        {step === 1 ? <Button className="bg-blue-600" onClick={() => setStep(2)} disabled={!data.plant_id || !data.cabinet_name}>Urmatorul</Button>
          : <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onComplete}><Zap className="w-4 h-4 mr-1" />Creaza</Button>}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddShelfDialog = ({ open, onOpenChange, cabinetName, data, setData, onAdd }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle>Adauga Raft la {cabinetName}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="text-zinc-400 text-xs">Nume Raft (optional)</Label><Input className="bg-zinc-800 border-zinc-700 text-white" value={data.name} onChange={e => setData({...data, name: e.target.value})} placeholder="Auto-generat" /></div>
        <div><Label className="text-zinc-400 text-xs">Nr. Pozitii</Label><Input type="number" className="bg-zinc-800 border-zinc-700 text-white" value={data.positions} onChange={e => setData({...data, positions: parseInt(e.target.value)||1})} /></div>
      </div>
      <DialogFooter><Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button><Button className="bg-emerald-600" onClick={onAdd}><Plus className="w-4 h-4 mr-1" />Adauga</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddPositionDialog = ({ open, onOpenChange, data, setData, onAdd }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle>Adauga Pozitie</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="text-zinc-400 text-xs">Nume (optional)</Label><Input className="bg-zinc-800 border-zinc-700 text-white" value={data.name} onChange={e => setData({...data, name: e.target.value})} /></div>
        <div><Label className="text-zinc-400 text-xs">Descriere</Label><Input className="bg-zinc-800 border-zinc-700 text-white" value={data.description} onChange={e => setData({...data, description: e.target.value})} /></div>
      </div>
      <DialogFooter><Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button><Button className="bg-emerald-600" onClick={onAdd}><Plus className="w-4 h-4 mr-1" />Adauga</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditCabinetDialog = ({ open, onOpenChange, currentName, newName, setNewName, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle>Redenumeste Dulap</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="text-zinc-400 text-xs">Actual</Label><Input className="bg-zinc-800 border-zinc-700 text-zinc-500" value={currentName || ''} disabled /></div>
        <div><Label className="text-zinc-400 text-xs">Nou</Label><Input className="bg-zinc-800 border-zinc-700 text-white" value={newName} onChange={e => setNewName(e.target.value.toUpperCase())} /></div>
      </div>
      <DialogFooter><Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button><Button className="bg-blue-600" onClick={onSave} disabled={!newName}>Salveaza</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const MoveShelfDialog = ({ open, onOpenChange, selectedNode, moveData, setMoveData, cabinets, onMove }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle>Muta Raft</DialogTitle><DialogDescription className="text-zinc-500">{selectedNode?.shelfName} din {selectedNode?.cabinetName}</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">De la: {selectedNode?.cabinetName} &rarr; {selectedNode?.shelfName}</div>
        <div><Label className="text-zinc-400 text-xs">Dulap Destinatie</Label>
          <Select value={moveData.target_cabinet} onValueChange={v => setMoveData({target_cabinet: v})}><SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Selectati" /></SelectTrigger>
            <SelectContent>{cabinets.filter(c => c !== selectedNode?.cabinetName).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <DialogFooter><Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button><Button className="bg-blue-600" disabled={!moveData.target_cabinet} onClick={onMove}><ArrowRightLeft className="w-4 h-4 mr-1" />Muta</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const QRDialog = ({ open, onOpenChange, bins, qrFieldOptions, qrFields, setQrFields, onExportQr, onExportQrZip, onExportCabinetQr, onExportShelfQr }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-zinc-900 border-zinc-700 text-white max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Export Coduri QR</DialogTitle></DialogHeader>
      <div className="space-y-3">
        {/* Cabinets & Shelves QR */}
        <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">QR Dulapuri & Rafturi</p>
        <div className="grid grid-cols-2 gap-2">
          {[{ f: 'large', t: 'QR Dulapuri', fn: onExportCabinetQr, icon: Archive }, { f: 'industrial', t: 'QR Rafturi', fn: onExportShelfQr, icon: Layers }].map(x => (
            <button key={x.t} className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 text-left transition-colors" onClick={() => x.fn?.(x.f)} data-testid={`qr-${x.t.toLowerCase().replace(/\s/g, '-')}`}>
              <x.icon className="w-4 h-4 text-blue-400 shrink-0" />
              <p className="text-sm font-medium text-blue-300">{x.t}</p>
            </button>
          ))}
        </div>

        <Separator className="bg-zinc-700" />

        <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">QR Pozitii BIN ({bins.length})</p>
        <div className="grid grid-cols-2 gap-2">
          {qrFieldOptions.map(f => (
            <div key={f.key} className="flex items-center space-x-2">
              <Checkbox id={`qr-${f.key}`} checked={qrFields[f.key]} onCheckedChange={c => setQrFields({...qrFields, [f.key]: c})} />
              <label htmlFor={`qr-${f.key}`} className="text-xs cursor-pointer text-zinc-300">{f.label}</label>
            </div>
          ))}
        </div>
        <div className="grid gap-2">
          {[{ f: 'standard', t: 'Standard (24/pg)', d: '3x8' }, { f: 'large', t: 'Mari (8/pg)', d: '2x4' }, { f: 'industrial', t: 'Industrial (16/pg)', d: '105x38mm' }, { f: 'thermal', t: 'Termic (50x30)', d: 'Termice' }].map(x => (
            <button key={x.f} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-left transition-colors" onClick={() => onExportQr(x.f)}>
              <Printer className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
              <div><p className="text-sm font-medium">{x.t}</p><p className="text-[10px] text-zinc-500">{x.d}</p></div>
            </button>
          ))}
          <Separator className="bg-zinc-700" />
          <button className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800 hover:bg-blue-500/10 border border-blue-500/20 text-left transition-colors" onClick={onExportQrZip}>
            <FileText className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <div><p className="text-sm font-medium text-blue-400">Export JPEG (ZIP)</p><p className="text-[10px] text-zinc-500">Imagini individuale</p></div>
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const ReportDialog = ({ open, onOpenChange, onExport }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle>Export Raport</DialogTitle></DialogHeader>
      <div className="space-y-3">
        {[{ t: 'all', l: 'Toate pozitiile' }, { t: 'occupied', l: 'Doar ocupate' }, { t: 'empty', l: 'Doar goale' }].map(r => (
          <div key={r.t}>
            <p className="text-xs font-semibold text-zinc-400 mb-1.5">{r.l}</p>
            <div className="flex gap-2">
              <button className="flex-1 p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-center transition-colors" onClick={() => onExport('excel', r.t)}>
                <FileSpreadsheet className="w-4 h-4 text-green-400 mx-auto mb-1" />Excel
              </button>
              <button className="flex-1 p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-center transition-colors" onClick={() => onExport('pdf', r.t)}>
                <FileText className="w-4 h-4 text-red-400 mx-auto mb-1" />PDF
              </button>
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const StocktakeDialog = ({ open, onOpenChange, data, setData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700 text-white max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><ArrowUpDown className="w-5 h-5 text-yellow-400" /> Inventar Rapid</DialogTitle>
        <DialogDescription className="text-zinc-400">Verificati si corectati cantitatile reale</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        {data.length === 0 ? (
          <p className="text-center text-zinc-500 py-8">Niciun stoc de inventariat</p>
        ) : data.map((d, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700" style={{ background: d.actual_qty !== d.system_qty ? 'rgba(234,179,8,0.05)' : T.subtle }}>
            <div className="flex-1"><p className="text-xs font-medium">{d.article_name}</p><p className="text-[10px] font-mono text-zinc-500">{d.bin_code} &middot; {d.shelf}/{d.position}</p></div>
            <div className="text-center"><p className="text-[10px] text-zinc-500">Sistem</p><p className="text-sm font-mono font-bold">{d.system_qty}</p></div>
            <div className="text-center"><p className="text-[10px] text-zinc-500">Real</p>
              <input type="number" className="w-20 h-8 text-center font-mono text-sm rounded bg-zinc-800 border border-zinc-600 text-white outline-none focus:border-yellow-500"
                value={d.actual_qty} onChange={e => { const v = parseFloat(e.target.value) || 0; setData(prev => prev.map((p, j) => j === i ? { ...p, actual_qty: v } : p)); }} /></div>
            {d.actual_qty !== d.system_qty && <span className={`text-xs font-mono font-bold ${d.actual_qty > d.system_qty ? 'text-emerald-400' : 'text-red-400'}`}>{d.actual_qty > d.system_qty ? '+' : ''}{(d.actual_qty - d.system_qty).toFixed(1)}</span>}
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button>
        <Button className="bg-yellow-600 hover:bg-yellow-700" onClick={onSubmit}>
          Finalizeaza ({data.filter(d => d.actual_qty !== d.system_qty).length} diferente)
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const TransferDialog = ({ open, onOpenChange, bins, form, setForm, onTransfer }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-blue-400" /> Transfer Rapid</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-zinc-400 text-xs">Dulap Sursa</Label>
            <Select value={form.from_cabinet} onValueChange={v => setForm({ ...form, from_cabinet: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="De la..." /></SelectTrigger>
              <SelectContent>{[...new Set(bins.map(b => b.cabinet))].sort().filter(c => c).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-zinc-400 text-xs">Dulap Destinatie</Label>
            <Select value={form.to_cabinet} onValueChange={v => setForm({ ...form, to_cabinet: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Catre..." /></SelectTrigger>
              <SelectContent>{[...new Set(bins.map(b => b.cabinet))].sort().filter(c => c && c !== form.from_cabinet).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button>
        <Button className="bg-blue-600" disabled={!form.from_cabinet || !form.to_cabinet} onClick={onTransfer}>Transfera</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const HistoryDialog = ({ open, onOpenChange, history }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700 text-white max-h-[80vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-purple-400" /> Istoric Miscari</DialogTitle></DialogHeader>
      <div className="space-y-1">
        {history.length === 0 ? <p className="text-center text-zinc-500 py-8">Nicio miscare</p> :
          history.map((m, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border-l-2" style={{ borderColor: m.movement_type === 'in' ? T.success : m.movement_type === 'out' ? T.danger : T.info, background: T.subtle }}>
              <span className={`text-xs font-mono font-bold w-16 ${m.movement_type === 'in' ? 'text-emerald-400' : m.movement_type === 'out' ? 'text-red-400' : 'text-blue-400'}`}>{m.movement_type?.toUpperCase()}</span>
              <div className="flex-1"><p className="text-xs">{m.article_name || 'Articol'}</p><p className="text-[10px] text-zinc-500">{m.from_bin_code} {m.to_bin_code && `→ ${m.to_bin_code}`}</p></div>
              <span className="text-xs font-mono font-bold">{m.quantity}</span>
            </div>
          ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const ImportDialog = ({ open, onOpenChange, onImport }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-400" /> Import BIN-uri din Excel</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="p-6 rounded-xl border-2 border-dashed border-zinc-600 hover:border-blue-500/50 transition-colors text-center">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
          <p className="text-sm text-zinc-400 mb-3">Selectati fisierul Excel</p>
          <input type="file" accept=".xlsx,.xls" onChange={onImport} className="w-full text-xs text-zinc-400" data-testid="import-file-input" />
        </div>
        <div className="p-3 rounded-lg bg-zinc-800 text-[11px] text-zinc-500">
          <p className="font-bold mb-1">Coloane: Dulap | Raft | Pozitie | Descriere | Stoc Minim</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const MinStockDialog = ({ open, onOpenChange, value, setValue, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-sm">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-yellow-400" /> Stoc Minim</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <Label className="text-zinc-400 text-xs">Prag minim (0 = dezactivat)</Label>
        <input type="number" min="0" value={value} onChange={e => setValue(parseFloat(e.target.value) || 0)}
          className="w-full h-10 px-3 text-lg font-mono text-center rounded-lg bg-zinc-800 border border-zinc-600 text-white outline-none focus:border-yellow-500" data-testid="min-stock-input" />
        <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-300">Sub aceasta valoare → alerta pe Dashboard</div>
      </div>
      <DialogFooter>
        <Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button>
        <Button className="bg-yellow-600" onClick={onSave}>Salveaza</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
